// Theme tokens — ported from /tmp/myams-design/myams/project/theme.jsx.

export type PaletteId = "amsterdam" | "canal" | "mondrian" | "riso";
export type RetroStyleId = "dutch-modernist" | "analog-70s" | "risograph";
export type TypePairId = "bricolage" | "archivo" | "redaction";
export type MapStyleId = "paper" | "illustrated" | "flat" | "pixel";

export interface Palette {
  label: string;
  paper: string;
  cream: string;
  ink: string;
  inkSoft: string;
  line: string;
  brick: string;
  mustard: string;
  canal: string;
  sky: string;
  pink: string;
}

export const MA_PALETTES: Record<PaletteId, Palette> = {
  amsterdam: {
    label: "Amsterdam Warm",
    paper: "#f0e4cc", cream: "#f5ecd6", ink: "#1a1208", inkSoft: "#5a4a36",
    line: "#a07a3e", brick: "#c83c28", mustard: "#e0a838", canal: "#2a5547",
    sky: "#5a8aa8", pink: "#e85a8a",
  },
  canal: {
    label: "Canal House",
    paper: "#ecdfc4", cream: "#f3e8cf", ink: "#1b1a14", inkSoft: "#4d4a3a",
    line: "#7a5a2a", brick: "#8a3018", mustard: "#d4912b", canal: "#1f4a3c",
    sky: "#4a7390", pink: "#d97a8a",
  },
  mondrian: {
    label: "Bold Primaries",
    paper: "#ece4d2", cream: "#f4ecd8", ink: "#0a0a0a", inkSoft: "#3a3a3a",
    line: "#1a1a1a", brick: "#d12815", mustard: "#f5c518", canal: "#1a4dc8",
    sky: "#1a4dc8", pink: "#d12815",
  },
  riso: {
    label: "Riso Pastel + Hot",
    paper: "#f1ead9", cream: "#f6f0e0", ink: "#2a1f3a", inkSoft: "#5a4e6a",
    line: "#8a7aa8", brick: "#ff4a3a", mustard: "#ffb84a", canal: "#3a8a7a",
    sky: "#5a7ad8", pink: "#ff5a9a",
  },
};

export interface RetroStyle {
  label: string;
  cardRadius: number;
  chipRadius: number;
  border: number;
  grain: "subtle" | "medium" | "heavy";
  accentShape: "square" | "rounded";
}

export const MA_RETRO_STYLES: Record<RetroStyleId, RetroStyle> = {
  "dutch-modernist": { label: "Dutch Modernist", cardRadius: 4,  chipRadius: 2,   border: 1.5, grain: "subtle", accentShape: "square" },
  "analog-70s":      { label: "70s Analog",      cardRadius: 14, chipRadius: 999, border: 1,   grain: "medium", accentShape: "rounded" },
  "risograph":       { label: "Risograph",       cardRadius: 6,  chipRadius: 4,   border: 1.5, grain: "heavy",  accentShape: "square" },
};

export interface TypePair {
  label: string;
  display: string;
  body: string;
  mono: string;
  displayWeight: number;
  displayTracking: number;
}

export const MA_TYPE_PAIRS: Record<TypePairId, TypePair> = {
  bricolage: {
    label: "Bricolage + Plex",
    display: '"Bricolage Grotesque", "Archivo Black", system-ui, sans-serif',
    body: '"IBM Plex Sans", system-ui, sans-serif',
    mono: '"JetBrains Mono", ui-monospace, monospace',
    displayWeight: 800, displayTracking: -0.02,
  },
  archivo: {
    label: "Archivo + Plex",
    display: '"Archivo Black", "Arial Black", sans-serif',
    body: '"IBM Plex Sans", system-ui, sans-serif',
    mono: '"DM Mono", ui-monospace, monospace',
    displayWeight: 900, displayTracking: -0.03,
  },
  redaction: {
    label: "Display Serif + Mono",
    display: '"DM Serif Display", "Playfair Display", Georgia, serif',
    body: '"IBM Plex Sans", system-ui, sans-serif',
    mono: '"JetBrains Mono", ui-monospace, monospace',
    displayWeight: 400, displayTracking: -0.02,
  },
};

export interface MapStyle { label: string; noise: boolean; illust: boolean; pixel: boolean; }
export const MA_MAP_STYLES: Record<MapStyleId, MapStyle> = {
  paper:       { label: "Retro Paper",   noise: true,  illust: false, pixel: false },
  illustrated: { label: "Illustrated",   noise: false, illust: true,  pixel: false },
  flat:        { label: "Flat Data-Viz", noise: false, illust: false, pixel: false },
  pixel:       { label: "Pixel CRT",     noise: false, illust: false, pixel: true  },
};

export interface Tweaks {
  palette: PaletteId;
  retroStyle: RetroStyleId;
  typePair: TypePairId;
  mapStyle: MapStyleId;
}

export const DEFAULT_TWEAKS: Tweaks = {
  palette: "amsterdam",
  retroStyle: "dutch-modernist",
  typePair: "bricolage",
  mapStyle: "paper",
};

export function maThemeVars(tweaks: Tweaks): Record<string, string> {
  const p = MA_PALETTES[tweaks.palette] ?? MA_PALETTES.amsterdam;
  const s = MA_RETRO_STYLES[tweaks.retroStyle] ?? MA_RETRO_STYLES["dutch-modernist"];
  const t = MA_TYPE_PAIRS[tweaks.typePair] ?? MA_TYPE_PAIRS.bricolage;
  return {
    "--ma-paper": p.paper,
    "--ma-cream": p.cream,
    "--ma-ink": p.ink,
    "--ma-ink-soft": p.inkSoft,
    "--ma-line": p.line,
    "--ma-brick": p.brick,
    "--ma-mustard": p.mustard,
    "--ma-canal": p.canal,
    "--ma-sky": p.sky,
    "--ma-pink": p.pink,
    "--ma-card-radius": s.cardRadius + "px",
    "--ma-chip-radius": s.chipRadius + "px",
    "--ma-border": s.border + "px",
    "--ma-display": t.display,
    "--ma-body": t.body,
    "--ma-mono": t.mono,
    "--ma-display-weight": String(t.displayWeight),
    "--ma-display-tracking": t.displayTracking + "em",
  };
}
